import { useTranslation } from 'react-i18next';

import type { TaskTrack } from '@/lib/task-track';
import type { Task } from '@/types/experience';

import AccountMenu from '@/components/nav/account-menu';
import NavBrand from '@/components/nav/nav-brand';
import NavBreadcrumb, { type Crumb } from '@/components/nav/nav-breadcrumb';
import QuickSettingsPopover from '@/components/nav/quick-settings-popover';
import TaskStepper from '@/components/task/task-stepper';

/** A task URL that the task page can actually load — see the stepper's own note. Exported so the
 *  test asserts the real builder rather than a copy of the rule. */
export function taskHref(taskId: string, experienceId?: string): string {
  return `/app/task/${taskId}${experienceId ? `?experience=${experienceId}` : ''}`;
}

interface TaskNavbarProps {
  experienceTitle: string;
  /**
   * Where this task sits structurally. Derived by the PAGE, not here, because the title bar
   * shows the same stepper at wider widths and the two must not compute it separately.
   */
  track?: TaskTrack | null;
  /** Every task, for the stepper's jump menu. */
  tasks?: Task[];
  /** The task being edited, so the jump menu can mark where you are. */
  taskId?: string;
  /**
   * Leaving the editor, guarded. Every destination in this bar goes through it, because leaving
   * with an unsaved change has to raise the modal — including a hop to a sibling task, which is
   * the same hazard as leaving for the experience.
   */
  onNavigate: (to: string) => void;
  /** Same guard, for exits that are not plain URLs — logout clears the token on its way. */
  onGuard?: (run: () => void) => void;
  experienceId?: string;
  collectionId?: string | null;
}

/**
 * The task editor's top bar.
 *
 * ## What it deliberately does NOT carry
 *
 * It had seven controls and three were duplicates, measured at 375px: the crumb trail alone
 * wanted 363px of a 222px slot, so the bar spent its width truncating itself.
 *
 * - **A Home crumb** — the Agavi logo already goes to `/app`, the same place. Identical
 *   destination, twice, side by side.
 * - **A back chevron** — it went to the experience, which is what the Experience crumb says it
 *   does. Its unsaved-changes guard was the one thing it owned, and that moved onto `onNavigate`
 *   so every destination here is guarded rather than just that one.
 * - **The collection crumb** — one hop from the logo, and the deepest page is where space is
 *   tightest. A deliberate divergence from "the breadcrumb includes the collection everywhere",
 *   which was decided when this bar had no stepper to fit.
 * - **The task's own title** — it was not even a link, and the title FIELD sits directly below
 *   it. It was also the only part of the trail whose width changed per task, which is what would
 *   have made the stepper beside it move between clicks.
 *
 * Net at 375px: 175px of duplication out, 98px of stepper in. The bar got roomier by gaining a
 * control.
 */
export default function TaskNavbar({
  experienceTitle, track, tasks, taskId, onNavigate, onGuard, experienceId, collectionId: _collectionId,
}: TaskNavbarProps) {
  const { t } = useTranslation();

  // One crumb, and it is clickable — `NavBreadcrumb` keys that off `to` rather than position,
  // which is what lets the last crumb be a destination now that the task is not in the trail.
  const crumbs: Crumb[] = [{
    label: experienceTitle || t('nav.newExperience'),
    to: experienceId ? `/app/experience/${experienceId}` : undefined,
  }];

  return (
    <header className='flex h-11 shrink-0 items-center gap-1 bg-ws-surface px-1.5 drop-shadow-[0px_2px_4px_rgba(0,0,0,0.06)]'>
      <NavBrand className='h-8' onNavigate={onNavigate} />

      <div className='relative flex min-w-0 flex-1 items-center gap-1'>
        <NavBreadcrumb items={crumbs} onNavigate={onNavigate} />
        {/*
          * `?experience=` IS REQUIRED. The task page reads its experience from the query string,
          * so a bare `/app/task/:id` renders "Missing experience context" — the same trap Phase 3
          * hit with Continue and the builder's Preview entry, and hit again here the first time
          * these arrows were clicked in a browser. Pinned by `task-navbar.test`.
          */}
        {/*
          * BELOW `xl`. The title bar carries it from 1280 up, centred on the CANVAS — which
          * pulls it left toward the title and so needs about 1150px before both fit. Under
          * that it lives here: at 1024 the canvas is 384px wide and would leave the title
          * ~70px, and at 768 the row has only ~48px spare in total.
          */}
        {track && (
          /* Centred in this lane rather than trailing the crumb. There is no editing column to
             centre on at these widths — the bar IS the full width — so the bar's own centre is
             the honest one, and it keeps the arrows in a constant place while the crumb beside
             them changes length. `pointer-events-none` on the frame so the empty space either
             side still belongs to the crumb.

             STATIC below `sm`, where centring does not fit: measured at 375px, a centred
             stepper runs from 114 and the crumb still ends at 153, and the only way to clear
             it would be to cut "Untitled experience" to about six characters. There it simply
             follows the crumb, which fits — 110px of crumb plus ~98 of stepper in a 258px
             lane. */
          <div className='pointer-events-none sm:absolute sm:inset-x-0 sm:flex sm:justify-center xl:hidden'>
            <div className='pointer-events-auto'>
              <TaskStepper track={track} tasks={tasks ?? []} currentTaskId={taskId ?? ''}
              onGo={id => onNavigate(taskHref(id, experienceId))} />
            </div>
          </div>
        )}
      </div>

      <div className='flex shrink-0 items-center'>
        <QuickSettingsPopover compact />
        <AccountMenu compact onGuard={onGuard} />
      </div>
    </header>
  );
}
